import React, { useState, useContext } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { compose } from 'recompose'
import { breakpoint, breakpoints } from 'src/theme/grid'
import connect from 'src/features/account/duck/connectors'
import { connectGlobalLoader } from 'src/ducks/connectors'
import Loader from 'src/components/LinearLoader'
import { connectMessagesBasicData } from 'src/features/messages/duck/connectors'
import { connectNotifications } from 'src/features/notifications/duck/connectors'
import { Location } from '@reach/router'
import MUIChatBubble from '@material-ui/icons/ChatBubbleOutline'
import MUIBurger from '@material-ui/icons/Menu'
import MUINotification from '@material-ui/icons/NotificationsOutlined'
import MUILeftArrow from '@material-ui/icons/KeyboardArrowLeft'
import MUIWarning from '@material-ui/icons/Warning'
import { Button } from '@material-ui/core'
import { NavigationList, NavigationElement } from './components/atoms'
import { AccountPopup, AccountAvatar } from './components/Account'
import { NAVIGATION } from './consts'
import { useIntl } from 'gatsby-plugin-intl'
import { useSelector } from 'react-redux'

export const HeaderContext = React.createContext({ onBurgerAction: () => {} })

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`

const DemoBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #b8860b;
  background: rgba(255, 193, 7, 0.12);
  border: 1px solid rgba(255, 193, 7, 0.35);
  white-space: nowrap;
  margin-left: 8px;
  margin-right: 8px;
  user-select: none;
  cursor: default;
  transition: all 0.2s ease;
  &:hover {
    background: rgba(255, 193, 7, 0.2);
    border-color: rgba(255, 193, 7, 0.5);
  }
  ${breakpoint.sm`
    font-size: 0.65rem;
    padding: 2px 8px;
  `}
`

const DemoBadgeDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #f0ad4e;
  animation: ${pulse} 1.5s ease-in-out infinite;
`

const Header = ({
  className,
  logoutUser,
  children,
  displayBurger = true,
  currentUser: { avatar, email, firstName, lastName },
  setMessagesListOpened,
  setNotificationsListOpened,
  notificationsListOpened,
  notificationsUnreadCount,
  listOpened,
  messagesCounters,
  isGlobalLoaderLoading
}) => {
  const [state, setState] = useState({
    fullNavigationVisible: false,
    activeNavigation: null
  })
  const intl = useIntl()
  const isDemoMode = process.env.GATSBY_DEMO === 'true'
  const { onBurgerAction } = useContext(HeaderContext)
  const stickyNotificationsList = useSelector(state => {
    const paymentNotifications = state.notifications.notificationsSticky
      .filter(
        notification => notification.code === 'company.payment.outstanding'
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

    return paymentNotifications.length > 0 ? [paymentNotifications[0]] : []
  })

  const toggle = type => {
    setState({
      //hide on click on the same icon
      fullNavigationVisible: !(state.activeNavigation === type),
      activeNavigation: state.activeNavigation === type ? null : type
    })
  }
  const setClose = () =>
    setState({ fullNavigationVisible: false, activeNavigation: null })
  const onClickOutsideNavigationDropdown = () => {
    //disable click outside on mobile
    const BREAKPOINT_S = parseInt(breakpoints.s.replace('px', ''), 10)
    window.innerWidth > BREAKPOINT_S &&
      setState({ ...state, activeNavigation: null })
  }

  return (
    <Location>
      {({ location }) => (
        <>
          <nav className={className}>
            <LoaderStyled loading={isGlobalLoaderLoading} />
            <ChildrenWrapper>
              {displayBurger && <Burger onClick={onBurgerAction} />}
              {children}
            </ChildrenWrapper>
            <NavigationListStyled
              fullNavigationVisible={state.fullNavigationVisible}
            >
              <NavigationElement>
                {isDemoMode && (
                  <DemoBadge>
                    <DemoBadgeDot />
                    {intl.formatMessage({ id: 'common.thisIsADemoVersion' })}
                  </DemoBadge>
                )}
                <LeftArrowIcon />
                <IconWrapper active={notificationsListOpened}>
                  <Notification
                    onClick={() =>
                      setNotificationsListOpened(!notificationsListOpened)
                    }
                  />
                  {notificationsUnreadCount > 0 && (
                    <Counter
                      onClick={() =>
                        setNotificationsListOpened(!notificationsListOpened)
                      }
                    >
                      {notificationsUnreadCount > 99
                        ? '99+'
                        : notificationsUnreadCount}
                    </Counter>
                  )}
                </IconWrapper>
                <IconWrapper active={listOpened}>
                  <MessagesIcon
                    onClick={() => setMessagesListOpened(!listOpened)}
                  />
                  {messagesCounters.allUnreadMessagesCount > 0 && (
                    <Counter onClick={() => setMessagesListOpened(!listOpened)}>
                      {messagesCounters.allUnreadMessagesCount}
                    </Counter>
                  )}
                </IconWrapper>
                <IconWrapper>
                  <AccountAvatar
                    url={avatar.url}
                    onClick={() => toggle(NAVIGATION.ACCOUNT)}
                    active={state.activeNavigation === NAVIGATION.ACCOUNT}
                  />
                  {state.activeNavigation === NAVIGATION.ACCOUNT && (
                    <AccountPopup
                      onClose={() => setClose()}
                      onClickOutside={onClickOutsideNavigationDropdown}
                      logoutUser={logoutUser}
                      active={state.activeNavigation === NAVIGATION.ACCOUNT}
                      location={location}
                      url={avatar.url}
                      name={`${firstName} ${lastName}`}
                      email={email}
                    />
                  )}
                </IconWrapper>
              </NavigationElement>
            </NavigationListStyled>
          </nav>
          {stickyNotificationsList.length > 0 && (
            <PaymentAlert
              key={stickyNotificationsList[0].id}
              body={stickyNotificationsList[0].body}
              invoiceUrl={stickyNotificationsList[0].cta.url}
            />
          )}
        </>
      )}
    </Location>
  )
}

const ConnectedComponent = compose(
  connect,
  connectMessagesBasicData,
  connectNotifications,
  connectGlobalLoader
)(Header)

export default styled(ConnectedComponent)`
  display: flex;
  height: ${({ theme }) => theme.dimensions.topBarHeight};
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  z-index: 100;
  background-color: white;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
  width: 100vw;
  ${breakpoint.m`
    width: 100%;
  `}
`

export const HeaderGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
  min-width: 0;
  padding-right: 83px;
  ${breakpoint.s`
      padding-right: 0;
  `}
`

const IconWrapper = styled.span`
  margin-right: 15px;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  ${({ active }) =>
    active &&
    css`
      &:after {
        content: '';
        display: block;
        position: absolute;
        top: -7px;
        left: -7px;
        width: 43px;
        height: 43px;
        opacity: 0.15;
        border-radius: 20px;
        background-color: #13a2bf;
      }
      ${MessagesIcon} {
        color: #13a2bf;
      }
    `}
  ${breakpoint.sm`
    margin-right: 1.7rem;
    display: inline-block;
  `}
`

const LeftArrowIcon = styled(MUILeftArrow)`
  && {
    font-size: 1.7rem;
  }
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  margin-right: 0.2rem;
  display: inline-block;
  ${breakpoint.s`
    display: none;
  `}
`

const MessagesIcon = styled(MUIChatBubble)`
  font-size: 1.7rem !important;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  transition: color 0.3s;
  margin-right: 0.4rem;
  position: relative;
  display: flex;
  z-index: 100;
  &:hover {
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
  }
`

const Notification = styled(MUINotification)`
  font-size: 1.7rem !important;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  transition: color 0.3s;
  margin-right: 0.4rem;
  &:hover {
    color: ${({ theme }) => theme.colors.easternBlue.hex()};
  }
`

const Burger = styled(MUIBurger)`
  font-size: 2.2rem !important;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  height: 100%;
  display: flex;
  opacity: 0.7;
  margin: 0 0.4rem;
  align-items: center;
  cursor: pointer;
  ${breakpoint.s`
    font-size: 2.4rem !important;
    margin: 0 1rem;
  `}
  ${breakpoint.m`
    display: none;
  `}
`

const NavigationListStyled = styled(NavigationList)`
  display: flex;
  padding-right: 0;
  position: absolute;
  right: 0;
  top: 0;
  background-color: white;
  min-width: 83px;
  ${breakpoint.s`
    padding-right: 30px;
    position: relative;
  `}
  ${IconWrapper} {
    display: none;
  }
  ${({ fullNavigationVisible }) =>
    fullNavigationVisible &&
    css`
      min-width: 180px;
      overflow: visible;
      ${IconWrapper} {
        display: flex;
      }
    `}
  ${IconWrapper}:last-child {
    display: flex;
  }
  ${breakpoint.s`
    min-width: auto;
    width: auto;
    ${IconWrapper} {
      display: flex;
    }
  `}
`
const ChildrenWrapper = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 0;
`

const Counter = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.fadedRed.hex()};
  position: absolute;
  top: -10px;
  right: -4px;
  overflow: hidden;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-size: 14px;
  font-weight: 600;
`

export const LoaderStyled = styled(Loader)`
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 10;
`

const PaymentAlertStyled = styled.div`
  width: 100%;
  font-size: 0.825rem;
  background-color: #ffebee;
  color: #d32f2f;
  padding: 12px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  gap: 12px;

  .alert-content {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 1;
    min-width: 0;

    span {
      word-wrap: break-word;
      hyphens: auto;
    }
  }

  .warning-icon {
    color: #d32f2f;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .payment-button {
    background-color: #d32f2f;
    color: white;
    white-space: nowrap;
    flex-shrink: 0;
    align-self: center;
    &:hover {
      background-color: #b71c1c;
    }
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 0.75rem;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;

    .alert-content {
      align-items: flex-start;
    }

    .payment-button {
      align-self: stretch;
      text-align: center;
      font-size: 0.8rem;
      padding: 10px;
      margin-top: 8px;
    }
  }
`

const PaymentAlert = ({ body, invoiceUrl }) => (
  <PaymentAlertStyled>
    <div className='alert-content'>
      <MUIWarning className='warning-icon' />
      <span>{body}</span>
    </div>
    <Button
      variant='contained'
      className='payment-button'
      href={invoiceUrl}
      target='_blank'
      rel='noopener noreferrer'
    >
      Pobierz fakturę
    </Button>
  </PaymentAlertStyled>
)
